import { forwardRef, ForwardedRef, ElementType } from 'react';

import { Button, ButtonProps } from '../Button/Button';
import { Icon, IconProps } from '../Icon/Icon';
import { FormProps } from '../../utils/forms/formProps';
import { useObjectRef } from '@react-aria/utils';
import { Tooltip, type TooltipProps } from '../Tooltip/Tooltip';
import { getBaseValue } from '../../utils/breakpoint/responsiveSSR';
import { useBreakpoint } from '../../utils/breakpoint/hooks/useBreakpoint';
import { resolveResponsiveProp } from '../../utils/breakpoint/responsive';
import type { ResponsiveValue } from '../../utils/breakpoint/responsiveSSR';

type TooltipPlacement = NonNullable<TooltipProps['placement']>;

export interface IconButtonProps<T extends ElementType = 'button'>
  extends Omit<ButtonProps<T>, 'children' | 'iconStartProps' | 'iconEndProps'>,
    FormProps {
  /**
   * What icon from fontAwesome to use with all options
   */
  iconProps?: IconProps;
  /**
   * Tooltip text to display on hover and focus
   */
  tooltipLabel?: string;
  /**
   * **[Responsive]** Tooltip placement relative to the button.
   * Supports a single placement value or a breakpoint object in code.
   */
  tooltipPlacement?: ResponsiveValue<TooltipPlacement>;
}

export const IconButton = forwardRef(function IconButton<
  T extends ElementType = 'button',
>(
  {
    iconProps = {
      iconName: 'circle',
    },
    tooltipLabel,
    tooltipPlacement = 'top',
    ...props
  }: IconButtonProps<T>,
  // Not using <T> here as typescript cannot narrow it properly from consuming component
  // see - https://www.tsteele.dev/posts/react-polymorphic-forwardref for potential
  // solutions if proper typing is required.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardedRef?: ForwardedRef<any>,
) {
  const ref = useObjectRef(forwardedRef);
  const currentBreakpoint = useBreakpoint();

  const { size } = props;
  const baseSize = getBaseValue(size, 'md');
  const resolvedTooltipPlacement =
    resolveResponsiveProp(tooltipPlacement, currentBreakpoint) ?? 'top';

  const finalIconProps = {
    ...iconProps,
    iconSize: baseSize === 'lg' ? '1.25x' : iconProps?.iconSize,
  };

  const iconButtonComponent = (
    <Button ref={ref} {...props} iconButton={true}>
      <Icon {...finalIconProps} data-testid="icon-button-icon" />
    </Button>
  );

  if (tooltipLabel) {
    return (
      <Tooltip label={tooltipLabel} placement={resolvedTooltipPlacement}>
        {iconButtonComponent}
      </Tooltip>
    );
  }

  return iconButtonComponent;
});

export default IconButton;
